import { z } from 'zod';

/** Organization and project slugs: lower-case ASCII letters, digits and hyphens. */
export const slug = z
  .string()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'lower-case letters, digits and hyphens');

export const sha256 = z.string().regex(/^[0-9a-f]{64}$/, 'a lower-case hex SHA-256 digest (64 characters)');

/**
 * Normalize a workspace path to the platform's canonical form: POSIX separators, Unicode NFC (macOS may hand us
 * NFD names such as "Suðurlandsbraut"), no leading "./". Returns the normalized string; validity is checked by
 * `filePath`.
 */
export function normalizePath(p: string): string {
  let s = p.normalize('NFC').replace(/\\/g, '/');
  while (s.startsWith('./')) s = s.slice(2);
  return s;
}

/** Why a (normalized) path is not acceptable, or null when it is. */
export function pathProblem(p: string): string | null {
  if (p.length === 0) return 'empty path';
  if (p.length > 1024) return 'path longer than 1024 characters';
  if (p.startsWith('/')) return 'path must be relative to the workspace root';
  if (p.endsWith('/')) return 'path must name a file, not a folder';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(p)) return 'control characters in path';
  if (p.includes('\\')) return 'backslash in path';
  for (const seg of p.split('/')) {
    if (seg === '') return 'empty path segment';
    if (seg === '.' || seg === '..') return '"." or ".." segment in path';
  }
  return null;
}

/** A file path inside a project workspace: relative, POSIX, NFC. Input is normalized first. */
export const filePath = z
  .string()
  .transform((s) => normalizePath(s))
  .superRefine((s, ctx) => {
    const problem = pathProblem(s);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

/** A path prefix for listings ("drawings/", "" for everything). */
export const pathPrefix = z
  .string()
  .max(1024)
  .transform((s) => normalizePath(s));

export const visibility = z.enum(['internal', 'publishable', 'restricted']);
export type Visibility = z.infer<typeof visibility>;

/** Visibilities a stored file may have (`restricted` files never leave the machine). */
export const storedVisibility = z.enum(['internal', 'publishable']);
export type StoredVisibility = z.infer<typeof storedVisibility>;

export const via = z.enum(['web', 'cli', 'mcp']);
export type Via = z.infer<typeof via>;

export const isoDateTime = z.iso.datetime({ offset: true });
